import React, { useState, useEffect } from 'react'
import s from './DriverInfoCard.module.scss'
import { getDriverDocuments } from 'service/AdminPanelService'

const DOC_LABELS = [
  'Фото ТС',
  'Паспорт (л)',
  'Паспорт (о)',
  'Права (л)',
  'Права (о)',
  'Тех.пасп. (л)',
  'Тех.пасп. (о)',
]

const PhoneLink = ({ phone }) => {
  if (!phone || phone === '-') return <span>—</span>
  return <a href={`tel:${phone}`} className={s.phoneLink}>📞 {phone}</a>
}

const StatusBadge = ({ label, colorKey }) => (
  <span className={`${s.statusBadge} ${s[colorKey] || ''}`}>{label}</span>
)

const InfoItem = ({ label, children }) => (
  <div className={s.infoItem}>
    <span className={s.infoLabel}>{label}</span>
    <div className={s.infoValue}>{children}</div>
  </div>
)

const getVehicleColorKey = (d) => {
  if (d.status === 'blocked' || d.status === 'Заблокирован') return 'blocked'
  if (d.status === 'in_route'   || d.status === 'В пути')    return 'route'
  if (d.status === 'in_loading' || d.status === 'В погрузке') return 'loading'
  return 'free'
}

/**
 * DriverInfoCard — shared driver detail card
 *
 * Props:
 *   driver  — driver object (fields: employee_name, employee_phone, phone,
 *             vehicle_number, vehicle_model, uid, is_active, status,
 *             status_display, created_at, loads_count, documents[])
 */
const DriverInfoCard = ({ driver }) => {
  const [docPreview, setDocPreview] = useState(null)
  const [docs, setDocs] = useState(null)      // null = not loaded yet
  const [docsLoading, setDocsLoading] = useState(false)
  const [docsError, setDocsError] = useState(null)

  useEffect(() => {
    if (!driver?.id) return
    // If documents are already embedded in the driver object, use them directly
    if (Array.isArray(driver.documents)) {
      setDocs(driver.documents)
      return
    }
    // Otherwise fetch from API
    setDocsLoading(true)
    setDocsError(null)
    getDriverDocuments(driver.id)
      .then(res => setDocs(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDocsError('Не удалось загрузить документы'))
      .finally(() => setDocsLoading(false))
  }, [driver?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!driver) return null

  const resolvedDocs = docs ?? []
  const byDesc = {}
  resolvedDocs.forEach(d => { if (d.description) byDesc[d.description] = d })
  const otherDocs = resolvedDocs.filter(d => !DOC_LABELS.includes(d.description))

  return (
    <div className={s.card}>

      {/* ── Информация о водителе ── */}
      <div className={s.section}>
        <div className={s.sectionTitle}>👤 Информация о водителе</div>
        <div className={s.infoGrid}>
          <InfoItem label="ФИО">
            {driver.employee_name || '—'}
          </InfoItem>
          <InfoItem label="Телефон">
            <PhoneLink phone={driver.employee_phone} />
          </InfoItem>
          <InfoItem label="Доп. телефон">
            <PhoneLink phone={driver.phone} />
          </InfoItem>
          <InfoItem label="Гос. номер">
            <strong>{driver.vehicle_number || '—'}</strong>
          </InfoItem>
          <InfoItem label="Модель">
            {driver.vehicle_model || '—'}
          </InfoItem>
          {driver.uid && (
            <InfoItem label="UID">
              <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{driver.uid}</span>
            </InfoItem>
          )}
          {driver.loads_count !== undefined && (
            <InfoItem label="Погрузок">
              <strong style={{ color: '#0d6efd' }}>{driver.loads_count}</strong>
            </InfoItem>
          )}
          <InfoItem label="Активность">
            <StatusBadge
              label={driver.is_active ? 'Активен' : 'Неактивен'}
              colorKey={driver.is_active ? 'active' : 'inactive'}
            />
          </InfoItem>
          <InfoItem label="Статус машины">
            <StatusBadge
              label={driver.status_display || driver.status || 'Свободен'}
              colorKey={getVehicleColorKey(driver)}
            />
          </InfoItem>
          {driver.created_at && (
            <InfoItem label="Зарегистрирован">
              {driver.created_at}
            </InfoItem>
          )}
        </div>
      </div>

      {/* ── Документы водителя ── */}
      <div className={s.section}>
        <div className={s.sectionTitle}>📎 Документы водителя</div>
        {docsLoading ? (
          <div className={s.docsEmpty}>Загрузка документов…</div>
        ) : docsError ? (
          <div style={{ padding: '10px 0', fontSize: 13, color: '#dc2626' }}>{docsError}</div>
        ) : (
          <>
            <div className={s.docsGrid}>
              {DOC_LABELS.map(label => {
                const doc = byDesc[label]
                const fileUrl = doc ? (doc.file_url || doc.file) : null
                const isActive = docPreview?.url === fileUrl && fileUrl
                return (
                  <button
                    key={label}
                    className={`${s.docBtn} ${doc ? s.docBtnHas : s.docBtnMissing} ${isActive ? s.docBtnActive : ''}`}
                    onClick={() => { if (!fileUrl) return; setDocPreview(isActive ? null : { url: fileUrl, label }) }}
                    title={doc ? `Открыть: ${label}` : `${label} — не загружено`}
                  >
                    <span className={s.docBtnIcon}>{doc ? '📄' : '—'}</span>
                    <span className={s.docBtnLabel}>{label}</span>
                  </button>
                )
              })}
              {otherDocs.map(doc => {
                const fileUrl = doc.file_url || doc.file
                const isActive = docPreview?.url === fileUrl
                return (
                  <button
                    key={doc.id}
                    className={`${s.docBtn} ${s.docBtnHas} ${isActive ? s.docBtnActive : ''}`}
                    onClick={() => setDocPreview(isActive ? null : { url: fileUrl, label: doc.description || doc.file_name || 'Файл' })}
                  >
                    <span className={s.docBtnIcon}>📁</span>
                    <span className={s.docBtnLabel}>{doc.description || doc.file_name || 'Файл'}</span>
                  </button>
                )
              })}
              {!resolvedDocs.length && (
                <div className={s.docsEmpty}>Документы не загружены</div>
              )}
            </div>

            {docPreview && (
              <div className={s.docPreview}>
                <div className={s.docPreviewHeader}>
                  <div className={s.docPreviewTitle}>
                    <span>📄</span>
                    <span>{docPreview.label}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={s.docPreviewOpen}>↗️ Открыть</a>
                    <button className={s.docPreviewClose} onClick={() => setDocPreview(null)}>✕</button>
                  </div>
                </div>
                <div className={s.docPreviewBody}>
                  <img
                    src={docPreview.url}
                    alt={docPreview.label}
                    className={s.docPreviewImg}
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block' }}
                  />
                  <div style={{ display: 'none', padding: '24px', textAlign: 'center' }}>
                    <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={s.docPreviewOpen}>⇩ Скачать файл</a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  )
}

export default DriverInfoCard
